import {
    PLATFORM_SUPPORT_SNAPSHOT_MAX_COUNT,
    PLATFORM_SUPPORT_SNAPSHOT_PICK_MAX_BYTES,
} from "../../../functions/src/api/constants";
import { functions } from "../../api/firebase";
import { resizeImage } from "../../api/imageUtils";

const ALLOWED_PICK_MIME_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const JPEG_CONTENT_TYPE = "image/jpeg";

export type PlatformSupportSnapshotSlotState = {
    slot: number;
    previewUrl: string;
    status: "uploading" | "ready" | "error";
    errorMessage?: string;
};

/** Reject non-images and oversize picks before decode/re-encode. */
export function validatePlatformSupportSnapshotPick(file: File): string | null {
    if (!ALLOWED_PICK_MIME_TYPES.has(file.type)) {
        return "invalidType";
    }
    if (file.size > PLATFORM_SUPPORT_SNAPSHOT_PICK_MAX_BYTES) {
        return "tooLarge";
    }
    return null;
}

/** Decode in browser so renamed executables / polyglots fail before upload. */
export async function reencodePlatformSupportSnapshot(file: File): Promise<Blob> {
    const objectUrl = URL.createObjectURL(file);
    try {
        await new Promise<void>((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => reject(new Error("invalidType"));
            img.src = objectUrl;
        });
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
    return resizeImage(file, 1600, 1600, 0.85);
}

async function uploadToSignedUrl(uploadUrl: string, body: Blob): Promise<void> {
    const res = await fetch(uploadUrl, {
        method: "PUT",
        body,
        headers: { "Content-Type": JPEG_CONTENT_TYPE },
    });
    if (!res.ok) {
        throw new Error(`Upload failed: ${res.status}`);
    }
}

export async function uploadPlatformSupportSnapshot(
    uploadSessionId: string,
    slot: number,
    file: File
): Promise<void> {
    const pickError = validatePlatformSupportSnapshotPick(file);
    if (pickError) {
        throw new Error(pickError);
    }
    const jpegBlob = await reencodePlatformSupportSnapshot(file);
    const getUrl = functions.httpsCallable("getPlatformSupportSnapshotUploadUrl");
    const { data } = await getUrl({
        uploadSessionId,
        slot,
        contentType: JPEG_CONTENT_TYPE,
    });
    const { uploadUrl } = data as { uploadUrl: string };
    await uploadToSignedUrl(uploadUrl, jpegBlob);
}

export async function deletePlatformSupportSnapshot(uploadSessionId: string, slot: number): Promise<void> {
    const del = functions.httpsCallable("deletePlatformSupportSnapshot");
    await del({ uploadSessionId, slot });
}

export function nextFreeSnapshotSlot(usedSlots: number[]): number | null {
    for (let slot = 0; slot < PLATFORM_SUPPORT_SNAPSHOT_MAX_COUNT; slot++) {
        if (!usedSlots.includes(slot)) {
            return slot;
        }
    }
    return null;
}

export { PLATFORM_SUPPORT_SNAPSHOT_MAX_COUNT };
